/* eslint-disable no-underscore-dangle */
import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  DEFAULT_THEME_COLOR,
  FETCH_ASSETS_URL,
} from 'appenv';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import isGroupVideoCallDisabled from 'components/chat/utils/isGroupVideoCallDisabled';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import LinkIcon from '@material-ui/icons/Link';
import InfoIcon from '@material-ui/icons/Info';

import { selectLocale } from 'models/localization';
import { Booth } from 'models/cms/booths';

import { TabsContext } from 'components/TabsContext';
import useEncodedMeetupUrl from 'hooks/useEncodedMeetupUrl';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import useJoinGroupChatSwitch from 'hooks/useJoinGroupChatSwitch';
import { isTwilioConnected } from 'models/twilio';
import GuideContent from 'pages/booths/components/BoothVideoCallGuideContent';
import useEmailContactsMemo from 'pages/booths/useEmailContactsMemo';
import { StyledModal as Modal } from 'pages/booths/boothDesign/styled';
import useGroupVideoCallSwitch from 'hooks/useGroupVideoCallSwitch';

const InsertInvitationIcon = `${FETCH_ASSETS_URL}/assets/calendarWhite.svg`;
const LivestreamIcon = `${FETCH_ASSETS_URL}/assets/livestream.svg`;
const videoCallSrc = `${FETCH_ASSETS_URL}/assets/videoCall.svg`;
const EmailIcon = `${FETCH_ASSETS_URL}/assets/emailWhite.svg`;
const chatroomIcon = `${FETCH_ASSETS_URL}/assets/chatroomWhite.svg`;
const CloseBtn = `${FETCH_ASSETS_URL}/assets/closeBtn.svg`;
const BUTTON_HEIGHT = 80;
const BOTTOM_OFFSET = 96;
const StyledIcon = styled.img`
  filter: grayscale(100%) brightness(900%);
`;

const BubbleBtnGroup = styled.div<{ $columnMode?: boolean }>`
  width: 100%;

  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;

  padding-right: 26px;
  background-color: rgba(0, 0, 0, 0);

  ${({ $columnMode }) => $columnMode && `
    padding: 16px;
    flex-direction: row;
    flex-wrap: wrap-reverse;
  `}
`;

const ButtonWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const BubbleBtnContainer = styled.div<{ $columnMode?: boolean; $order: number }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: ${BUTTON_HEIGHT}px;
  order: ${({ $order }) => $order};

  ${({ $columnMode }) => $columnMode && `
    flex-direction: row-reverse;
    flex-basis: 50%;
    max-width: 50%;
    padding: 0 4px;
    .modal-bubble-btn-text {
      margin-right: 0;
      margin-left: 8px;
    }
    .guide-info-btn {
      margin-right: 0;
      margin-left: 4px;
    }
  `}
`;

const BubbleBtnText = styled.div`
  font-size: 14px;
  line-height: 14px;
  color: #282B40;
  margin-right: 16px;

  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  word-wrap: break-word;

  @supports (-webkit-line-clamp: 2) {
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
BubbleBtnText.defaultProps = {
  className: 'modal-bubble-btn-text',
};

const BubbleBtn = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 48px;
  line-height: 65px;
  color: #FFF;
  background: ${({ color }) => color};
  text-align: center;
  margin: 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;

  flex-basis: 48px;
  flex-shrink: 0;
`;

const StyledModal = styled(Modal)`
  background-color: rgba(255, 255, 255, 0.89);
  width: 100%;
  height: 100%;

  & > div {
    background-color: rgba(0, 0, 0, 0) !important;

    &:focus {
      outline: none;
    }
  }
`;

const StyledCircularLoadingIndicator = styled(CircularLoadingIndicator)`
  display: flex;
`;

const InfoBtn = styled.div`
  margin-right: 8px;
  color: #A2AAB3;
`;

const CloseButton = styled(BubbleBtn)`
  position: absolute;
  right: 20px;
  bottom: 20px;
  background: #CF2F2F;
  margin: 0;

  height: 60px;
  width: 60px;
`;

const OverflowWrapper = styled.div`
  overflow-y: auto;
  max-height: 100%;
  width: 100%;
  position: absolute;
  bottom: 0;
`;

const OuterWrapper = styled.div`
  height: calc(100% - ${BOTTOM_OFFSET}px);
  position: relative;
`;
export interface MobileProfileModalProps {
  booth: Booth;
  modalOpen: boolean;
  closeModal: () => void;
}

const isEmail = (email: string) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const handleLinkClick = (actionExternalLinkUrl: string) => {
  let link = actionExternalLinkUrl;
  if (isEmail(actionExternalLinkUrl)) {
    link = `mailto:${actionExternalLinkUrl}`;
  } else if (actionExternalLinkUrl.startsWith('http://') || actionExternalLinkUrl.startsWith('https://')) {
    link = actionExternalLinkUrl;
  } else {
    link = `//${actionExternalLinkUrl}`;
  }
  window.open(link, '_blank');
};

const MobileProfileModal: FC<MobileProfileModalProps> = ({
  booth,
  modalOpen,
  closeModal,
}) => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const {
    features,
    themeColor = DEFAULT_THEME_COLOR,
    chatroomConfig,
  } = useSelector((s) => s.eventConfigs);
  const mobileExternalLink = useMemo(() => features.includes('mobileExternalLink'), [features]);

  const overflowRef = useRef<HTMLDivElement>();
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [isOverflow, setOverflow] = useState(false);

  const { livestreams, videoCallUrl, appointmentBookingUrl } = booth;
  const encodedMeetupUrl = useEncodedMeetupUrl(appointmentBookingUrl);
  const createGroupVideoCall = useGroupVideoCallSwitch();
  const { setActiveTabKey } = useContext(TabsContext);
  const twilioConnected = useSelector(isTwilioConnected);
  const { joinBoothChat } = useJoinGroupChatSwitch();
  const emailContacts = useEmailContactsMemo(booth.rootContentElement?.tabs || []);

  const externalLinkLayers = useMemo(() => {
    if (!mobileExternalLink) return [];
    const layers = (booth?.boothDesignCanvas?.layers || [])
      .filter((it) => it.__typename === 'BoothDesignImageButtonElement' && it.actionType === 'EXTERNAL_LINK');
    return layers;
  }, [booth, mobileExternalLink]);

  const isVideoCallDisabled = useMemo(() => isGroupVideoCallDisabled(chatroomConfig), [chatroomConfig]);

  const emailString = useMemo(() => {
    if (emailContacts && emailContacts.length > 0) {
      const email = emailContacts.find((eachemailContact) => (eachemailContact.type === 'email'));
      if (email?.contact) return email.contact;
    }
    return '';
  }, [emailContacts]);

  const boothNameForMatomo = useMemo(() => findAvailableLocale(booth?.name || {}, 'en'), [booth]);

  const goLiveStreamTab = useCallback(() => {
    const targetIndex = livestreams?.[0]?.index || 0;
    setActiveTabKey(targetIndex);
    document.getElementById('booth-container')
      .scroll({
        top: 1000,
        left: 0,
        behavior: 'smooth',
      });
    closeModal();
  }, [setActiveTabKey, closeModal, livestreams]);

  const openChatWindow = useCallback(() => {
    window.trackingItemToFirebase(null, 'Booth', 'Chat', 'Group_Chat_Click', 'Booth_Profile', booth.id, boothNameForMatomo);
    closeModal();
    const isAutoJoinMsgDisabled = chatroomConfig?.isAutoJoinMsgDisabled;
    joinBoothChat(booth.id, booth.name[locale], !isAutoJoinMsgDisabled);
  }, [booth, boothNameForMatomo, closeModal, chatroomConfig, joinBoothChat, locale]);

  const clickMeetup = useCallback(() => {
    window.trackingItemToFirebase(null, 'Booth', 'Meetup', 'Meetup_Click', 'Booth_Profile', booth.id, boothNameForMatomo);
    handleLinkClick(encodedMeetupUrl);
  }, [booth, boothNameForMatomo, encodedMeetupUrl]);

  const clickLeaveMessage = useCallback(() => {
    window.trackingItemToFirebase(null, 'Booth', 'Leave a Message', 'Leave a Message_Click', 'Booth_Profile', booth.id, boothNameForMatomo);
    handleLinkClick(emailString);
  }, [booth, boothNameForMatomo, emailString]);

  const legacyOpenGroupVideoCall = useCallback(() => handleLinkClick(videoCallUrl), [videoCallUrl]);

  const openGuide = useCallback(() => setGuideModalOpen(true), []);
  const closeGuide = useCallback(() => setGuideModalOpen(false), []);

  const startVideoCall = useCallback(
    () => createGroupVideoCall(booth.name[locale], booth.id),
    [booth, createGroupVideoCall, locale],
  );
  interface ModalButton {
    condition: boolean;
    displayNode: ReactNode;
  }

  const modalButtons = useMemo((): ModalButton[] => [
    {
      condition: !isVideoCallDisabled,
      displayNode: features.includes('groupVideoCall') ? (
        <>
          <InfoBtn className="guide-info-btn">
            <InfoIcon onClick={openGuide} />
            <Modal
              open={guideModalOpen}
              onClose={closeGuide}
              aria-labelledby="video-call-guide-content"
              aria-describedby="video-call-guide-content-modal"
            >
              <GuideContent handleClose={closeGuide} isDesktop={false} />
            </Modal>
          </InfoBtn>
          <BubbleBtnText>
            {t('booth.video_call', 'Video Call')}
          </BubbleBtnText>
          <BubbleBtn color={themeColor} onClick={startVideoCall}>
            <StyledIcon src={videoCallSrc} />
          </BubbleBtn>
        </>
      ) : (
        <>
          {videoCallUrl && (
            <>
              <BubbleBtnText>
                {t('booth.video_call', 'Video Call')}
              </BubbleBtnText>
              <BubbleBtn color={themeColor} onClick={legacyOpenGroupVideoCall}>
                <StyledIcon src={videoCallSrc} />
              </BubbleBtn>
            </>
          )}
        </>
      ),
    },
    {
      condition: livestreams && livestreams.length > 0,
      displayNode: (
        <>
          <BubbleBtnText>
            {t('booth.live_stream', 'Livestream')}
          </BubbleBtnText>
          <BubbleBtn color={themeColor} onClick={goLiveStreamTab}>
            <StyledIcon src={LivestreamIcon} />
          </BubbleBtn>
        </>
      ),
    },
    {
      condition: Boolean(booth.meetupEnabled && booth.appointmentBookingUrl),
      displayNode: (
        <>
          <BubbleBtnText>
            {t('booth.meetup', 'Meetup')}
          </BubbleBtnText>
          <BubbleBtn color={themeColor} onClick={clickMeetup}>
            <StyledIcon src={InsertInvitationIcon} alt={t('booth.meetup', 'Meetup')} />
          </BubbleBtn>
        </>
      ),
    },
    {
      condition: Boolean(emailString),
      displayNode: (
        <>
          <BubbleBtnText>
            {t('booth.leave_a_message', 'Leave a Message')}
          </BubbleBtnText>
          <BubbleBtn color={themeColor} onClick={clickLeaveMessage}>
            <StyledIcon src={EmailIcon} alt={t('booth.leave_a_message', 'Leave a Message')} />
          </BubbleBtn>
        </>
      ),
    },
    {
      condition: !isChatroomDisabled(chatroomConfig),
      displayNode: (
        <>
          <BubbleBtnText>
            {t('booth.chat', 'Chat')}
          </BubbleBtnText>
          <BubbleBtn color={themeColor} onClick={openChatWindow}>
            {
              (twilioConnected) ? (
                <StyledIcon src={chatroomIcon} alt="chatroom" />
              ) : (
                <StyledCircularLoadingIndicator size={25} color="#fff" />
              )
            }
          </BubbleBtn>
        </>
      ),
    },
  ].filter(({ condition }) => condition).reverse(), [
    booth.appointmentBookingUrl,
    booth.meetupEnabled,
    chatroomConfig,
    clickLeaveMessage,
    clickMeetup,
    closeGuide,
    emailString,
    features,
    goLiveStreamTab,
    guideModalOpen,
    isVideoCallDisabled,
    legacyOpenGroupVideoCall,
    livestreams,
    openChatWindow,
    openGuide,
    startVideoCall,
    t,
    themeColor,
    twilioConnected,
    videoCallUrl,
  ]);

  const externalActionButtons = useMemo((): ModalButton[] => {
    if (!mobileExternalLink) return [];
    return externalLinkLayers.map((layer) => ({
      condition: true,
      displayNode: (
        <>
          <BubbleBtnText>
            {layer.keyName}
          </BubbleBtnText>
          <BubbleBtn color={themeColor} onClick={() => handleLinkClick(layer.actionExternalLinkUrl)}>
            <LinkIcon />
          </BubbleBtn>
        </>
      ),
    }));
  }, [externalLinkLayers, mobileExternalLink, themeColor]);

  useLayoutEffect(() => {
    const { clientHeight } = document.getElementById('root');
    const actualHeight = BUTTON_HEIGHT * (modalButtons.length + externalActionButtons.length);
    setOverflow(actualHeight > (clientHeight - BOTTOM_OFFSET));
  }, [modalOpen, modalButtons, externalActionButtons]);

  useLayoutEffect(() => {
    if (modalOpen) {
      setTimeout(() => {
        if (overflowRef.current) {
          const overflowElement = overflowRef.current;
          overflowElement.scrollTop = overflowElement.scrollHeight - overflowElement.clientHeight;
        }
      }, 100);
    }
  }, [modalOpen]);

  return (
    <StyledModal
      open={modalOpen}
      onClose={closeModal}
      aria-labelledby="mobile-profile-modal"
      aria-describedby="mobile-profile-interactive-buttons"
    >
      <ButtonWrapper>
        <OuterWrapper>
          <OverflowWrapper ref={overflowRef}>
            <BubbleBtnGroup $columnMode={isOverflow}>
              {modalButtons.map(({ displayNode }, index) => {
                const order = isOverflow
                  ? 2 * index + 1
                  : index;
                return (
                  <BubbleBtnContainer key={`ModalButton-${order}`} $columnMode={isOverflow} $order={order}>
                    {displayNode}
                  </BubbleBtnContainer>
                );
              })}
              {externalActionButtons.map(({ displayNode }, index) => {
                const order = isOverflow
                  ? 2 * index
                  : modalButtons.length + index;
                return (
                  <BubbleBtnContainer key={`ModalButton-${order}`} $columnMode={isOverflow} $order={order}>
                    {displayNode}
                  </BubbleBtnContainer>
                );
              })}
            </BubbleBtnGroup>
          </OverflowWrapper>
        </OuterWrapper>
        <CloseButton onClick={closeModal} color={themeColor}>
          <StyledIcon src={CloseBtn} alt="Close" />
        </CloseButton>
      </ButtonWrapper>
    </StyledModal>
  );
};

export default MobileProfileModal;
